import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import aiChatIcon from "../../../assets/img/AI_Chat_Assistant_Icon_round.svg";
import { Tooltip } from "@mui/material";
import { trackActivity } from "../../../utils/mixpanel";

const STORAGE_KEY = "ai-chat-launcher-position";
const DRAG_THRESHOLD = 5;
const LAUNCHER_WIDTH = 56;
const LAUNCHER_HEIGHT = 67;
const MARGIN = 24;

function getDefaultPosition() {
  return {
    x: window.innerWidth - LAUNCHER_WIDTH - MARGIN,
    y: window.innerHeight - LAUNCHER_HEIGHT - MARGIN
  };
}

function clampPosition(position) {
  return {
    x: Math.min(Math.max(position.x, 0), window.innerWidth - LAUNCHER_WIDTH),
    y: Math.min(Math.max(position.y, 0), window.innerHeight - LAUNCHER_HEIGHT)
  };
}

function eventPoint(event) {
  if (event.touches && event.touches.length) {
    return { x: event.touches[0].clientX, y: event.touches[0].clientY };
  }
  return { x: event.clientX, y: event.clientY };
}

const AiChatLauncher = () => {
  const navigate = useNavigate();
  const dragState = useRef({ dragging: false, moved: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const [position, setPosition] = useState(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return clampPosition(JSON.parse(saved));
      } catch (e) {
        return getDefaultPosition();
      }
    }
    return getDefaultPosition();
  });

  useEffect(() => {
    const handleResize = () => setPosition((prev) => clampPosition(prev));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMove = useCallback((event) => {
    if (!dragState.current.dragging) return;
    const { x, y } = eventPoint(event);
    const dx = x - dragState.current.startX;
    const dy = y - dragState.current.startY;
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      dragState.current.moved = true;
    }
    setPosition(clampPosition({ x: dragState.current.originX + dx, y: dragState.current.originY + dy }));
  }, []);

  const stopDragging = useCallback(() => {
    if (!dragState.current.dragging) return;
    dragState.current.dragging = false;
    window.removeEventListener("mousemove", handleMove);
    window.removeEventListener("mouseup", stopDragging);
    window.removeEventListener("touchmove", handleMove);
    window.removeEventListener("touchend", stopDragging);
    setPosition((prev) => {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prev));
      return prev;
    });
    if (!dragState.current.moved) {
      trackActivity("Item Clicked", {
        page_title: "AI Chat Assistant",
        page_type: "Launcher"
      });
      navigate("/ai-chat");
    }
  }, [handleMove, navigate]);

  const startDragging = useCallback((event) => {
    const { x, y } = eventPoint(event);
    dragState.current = {
      dragging: true,
      moved: false,
      startX: x,
      startY: y,
      originX: position.x,
      originY: position.y
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", stopDragging);
  }, [position, handleMove, stopDragging]);

  const handleMouseDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    startDragging(event);
  };

  const handleTouchStart = (event) => {
    startDragging(event);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", stopDragging);
    };
  }, [handleMove, stopDragging]);

  // Hide only on AI app routes (/ai-chat, /ai-token-*), not Settings (/settings/ai-consent).
  const hideAiLauncher = /^\/ai-/.test(window.location?.pathname || "");

  if (hideAiLauncher) return null;

  return (
    <Tooltip arrow placement="top" title="AI Chat Assistant">
      <button
        type="button"
        className="ai-chat-launcher"
        title="Ask Lease AI"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          position: "fixed",
          left: position.x,
          top: position.y,
          zIndex: 1300,
          width: LAUNCHER_WIDTH,
          height: LAUNCHER_HEIGHT,
          padding: 0,
          margin: 0,
          border: "none",
          background: "transparent",
          boxShadow: "none",
          outline: "none",
          cursor: "grab",
          touchAction: "none",
          userSelect: "none",
          WebkitAppearance: "none",
          appearance: "none",
          lineHeight: 0
        }}
      >
        <img
          src={aiChatIcon}
          alt="Ask Lease AI"
          draggable={false}
          style={{
            display: "block",
            width: LAUNCHER_WIDTH,
            height: LAUNCHER_HEIGHT,
            pointerEvents: "none"
          }}
        />
      </button>
    </Tooltip>
  );
};

export default AiChatLauncher;
